import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { COURSE_DETAILS_MAP } from "./courseData";
import EnrollmentForm from '../../enrollment/EnrollmentForm';
import styles from "./CourseDetails.module.css";
import { ArrowLeft, Clock, Star, Users, ChevronDown } from "lucide-react";

const CourseDetails = () => {
  const { courseId } = useParams();
  const [isModalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const [level, setLevel] = useState<"basic" | "intermediate" | "advanced">(
    "basic"
  );

  const courseData = COURSE_DETAILS_MAP[courseId || "oil-painting"];
  const currentLevelData = courseData.levels[level];

  return (
    <div className={styles.pageWrapper}>
      {/* 1. BACK BUTTON: Moved out of the flow to save vertical space */}
      <button className={styles.floatingBackBtn} onClick={() => navigate(-1)}>
        <ArrowLeft size={16} />
        <span>Back</span>
      </button>

      <header className={styles.courseHero}>
        <div className={styles.titleRow}>
          <div className={styles.titleArea}>
            <div className={styles.headerFlexGroup}>
              <h1>{courseData.title}</h1>
            </div>
          </div>

          <div className={styles.compactStats}>
            {/* Duration */}
            <div className={styles.miniStat}>
              <span className={styles.statLabel}>Duration</span>
              <div className={styles.statValue}>
                <Clock size={14} /> 14 weeks
              </div>
            </div>

            {/* Rating */}
            <div className={styles.miniStat}>
              <span className={styles.statLabel}>Rating</span>
              <div className={styles.statValue}>
                <Star size={14} className={styles.ratingStar} /> 4.9/5.0
              </div>
            </div>

            {/* Students */}
            <div className={styles.miniStat}>
              <span className={styles.statLabel}>Students</span>
              <div className={styles.statValue}>
                <Users size={14} /> 7 per batch
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className={styles.mainContainer}>
        {/* LEFT SIDEBAR */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarSection}>
            <h3>Course Level</h3>
            <div className={styles.levelToggle}>
              {(["basic", "intermediate", "advanced"] as const).map((l) => (
                <button
                  key={l}
                  className={
                    level === l ? styles.activeLevel : styles.inactiveLevel
                  }
                  onClick={() => setLevel(l)}
                >
                  {l.charAt(0).toUpperCase() + l.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className={styles.sidebarSection}>
            <h3>{level.toUpperCase()} Summary</h3>
            <p className={styles.overviewText}>{currentLevelData.synopsis}</p>
          </div>
        </aside>

        {/* CENTER SYLLABUS: Matching your screenshot nested design */}
        <section className={styles.contentBody}>
          {/* NEW: Curriculum Header Band */}
          <div className={styles.curriculumBand}>
            <div className={styles.bandItem}>
              <span className={styles.bandLabel}>Class Duration</span>
              <span className={styles.bandValue}>2 Hours</span>
            </div>
            <div className={styles.bandItem}>
              <span className={styles.bandLabel}>Frequency</span>
              <span className={styles.bandValue}>1 class / Week</span>
            </div>
            <div className={styles.bandItem}>
              <span className={styles.bandLabel}>Payment Cycle</span>
              <span className={styles.bandValue}>Monthly</span>
            </div>
            <div className={styles.bandItem}>
              <span className={styles.bandLabel}>Max Students</span>
              <span className={styles.bandValue}>7 per batch</span>
            </div>
          </div>

          <div className={styles.accordionContainer}>
            {/* Accordion 2: Learning Curriculum */}
            <details className={styles.accordion} open>
              <summary className={styles.accordionHeader}>
                <h3>Learning Curriculum</h3>
                <span className={styles.lessonCount}>
                  {currentLevelData.syllabus.length} Lessons
                </span>
                <ChevronDown size={18} className={styles.chevronIcon} />
              </summary>

              <div className={styles.accordionWrapper}>
                <div className={styles.accordionContent}>
                  {/* Flex container for Syllabus + Image */}
                  <div className={styles.curriculumLayout}>
                    {/* Left/Right: The Syllabus List */}
                    <div className={styles.syllabusSide}>
                      <ul className={styles.syllabusList}>
                        {currentLevelData.syllabus.map(
                          (item: any, i: number) => (
                            <li key={i} className={styles.syllabusGroup}>
                              <div className={styles.mainItem}>
                                <Star
                                  size={18}
                                  className={styles.starIcon}
                                  fill="#fca311"
                                  color="#fca311"
                                />
                                <span>
                                  {typeof item === "string" ? item : item.title}
                                </span>
                              </div>
                              {item.subItems && (
                                <ul className={styles.nestedList}>
                                  {item.subItems.map(
                                    (sub: string, j: number) => (
                                      <li key={j} className={styles.nestedItem}>
                                        <Star
                                          size={14}
                                          className={styles.starIcon}
                                          fill="#fca311"
                                          color="#fca311"
                                        />
                                        <span>{sub}</span>
                                      </li>
                                    )
                                  )}
                                </ul>
                              )}
                            </li>
                          )
                        )}
                      </ul>
                    </div>

                    {/* Left/Right: The Course Image */}
                    <div className={styles.imageSide}>
                      <img
                        src={currentLevelData.image}
                        alt="Course Preview"
                        className={styles.curriculumImage}
                      />
                      <div className={styles.imageCaption}>
                        Visualizing the techniques
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </details>
          </div>
        </section>

        {/* RIGHT PAYMENT */}
        <section
          className={styles.paymentPanel}
          style={{ borderTop: `6px solid ${currentLevelData.color}` }}
        >
          <div className={styles.priceBox}>
            <span className={styles.enrollLabel}>Full Access: {level}</span>
            <h1
              className={styles.price}
              style={{ color: currentLevelData.color }}
            >
              {currentLevelData.price}
            </h1>
          </div>
          <div className={styles.benefits}>
            <p>✓ Lifetime Access</p>
            <p>✓ Certificate of Completion</p>
            <p>✓ Resources</p>
          </div>
          <button
            className={styles.enrollBtn}
            style={{ backgroundColor: currentLevelData.color }}
            onClick={() => setModalOpen(true)}
          >
            Enroll Now
          </button>
          {isModalOpen && (
        <EnrollmentForm
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
          selectedProgram="Fine-Arts"
        />
      )}
        </section>
      </div>
    </div>
  );
};

export default CourseDetails;
